import { _ } from "libs";

import React, { useState, useEffect } from "react";
import Tabs from "./tabs";
import withRedux from "../../withRedux";
import ReservationsList from "../../components/reservationsList";
import { Reservation, Club, AdLocation } from "../../types";
import ReservationDetail from "../../components/reservationDetail";
import SeasonStats from "../../components/seasonStats";
import {
	cancelReservation,
	editReservation,
	getClub,
	getReservationContext,
	cancelEditReservation,
	getReservation,
	ReservationsLoader,
} from "../../actions";
import AdWidget from "../../components/adWidget";
import Loader from "../../components/loader";
import { AuthState } from "../../reducers/auth";
import { ReservationState } from "../../reducers/reservation";
import BookingWizard from "../../components/club/bookingWizard/bookingWizard";
import { InvisibleInfiniteLoader } from "../../components/infiniteLoader";
function findReservation(id: string, reservations: Reservation[]): Reservation | null {
	for (let reservation of reservations) {
		if (reservation.reservationId === id) {
			return reservation;
		}
	}

	return null;
}

const PAGE_LIMIT = 50;

type Props = {
	auth: AuthState;
	reservation: ReservationState;
	reservationsLoader: ReservationsLoader;
	id?: string;
	past?: boolean;
	editReservation: typeof editReservation;
	cancelEditReservation: typeof cancelEditReservation;
	getReservationContext: typeof getReservationContext;
};

const Reservations = ({
	auth,
	reservation,
	reservationsLoader,
	id = null,
	past = false,
	editReservation,
	cancelEditReservation,
	getReservationContext,
}: Props) => {
	const [reservations, setReservations] = useState([] as Reservation[]);
	const [selectedReservation, selectReservation] = useState(null as Reservation);
	const [loading, setLoading] = useState(true);
	const [currentPage, setPage] = useState(0);
	const [hasMoreResults, setHasMoreResults] = useState(true);

	const [editLoading, setEditLoading] = useState(false);
	const [editing, setEditing] = useState(false);

	const [club, setClub] = useState(null as Club);

	const reloadReservations = (page) => {
		reservationsLoader(page, PAGE_LIMIT).then(({ reservations: data, pageCurrent, pageLimit, pageRows }) => {
			setReservations([...reservations, ...data]);
			setLoading(false);
			if (data.length === 0) {
				setHasMoreResults(false);
			}

			if (id && !selectedReservation) {
				const reservation = findReservation(id, data);
				if (reservation) {
					selectReservation(reservation);
				}
			}
		});
	};

	useEffect(() => {
		reloadReservations(currentPage);
	}, [currentPage]);

	const onViewChange = async (inView: boolean, observerEntry) => {
		const shouldLoad = inView && hasMoreResults && !loading;

		if (shouldLoad) {
			setPage(currentPage + 1);
		}
	};

	const cancel = async (uuid: string) => {
		if (window.confirm(_("reservations.cancel.prompt"))) {
			setLoading(true);
			selectReservation(null);
			await cancelReservation(uuid);
			removeReservation(uuid);
			setLoading(false);
		}
	};

	const removeReservation = (uuid: string) => {
		const remainingReservations = reservations.filter((res) => res.reservationId !== uuid);
		setReservations(remainingReservations);
	};

	const edit = async (reservation: Reservation) => {
		setEditLoading(true);
		setEditing(true);

		editReservation(reservation);
		const club = await getClub(reservation.club.clubInfo.clubId);
		setClub(club);

		await getReservationContext({});

		setEditLoading(false);
	};

	const onReservationSelect = (reservation: Reservation) => {
		if (!editing) {
			selectReservation(reservation);
			setEditLoading(false);
			cancelEditReservation();
		}
	};

	const editClose = (reservation: Reservation) => {
		selectReservation(reservation);
		setEditing(false);
		cancelEditReservation();
	};

	return (
		<div className="reservationsPage hasSubheader">
			<Tabs />
			<div className="wrap">
				<div className="contentWrapper">
					<div className="contentLeft">
						<ReservationsList
							userId={auth.userId}
							reservations={reservations}
							selectedReservation={selectedReservation}
							onSelect={onReservationSelect}
							loading={loading}
						/>
						<InvisibleInfiniteLoader onViewChange={onViewChange} />
						{loading ? <Loader /> : null}
					</div>
					<div className="contentRight">
						{selectedReservation ? (
							editing && club ? (
								<BookingWizard
									{...{
										reservation,
										club,
										courts: club.courts,
									}}
									edit={editing}
									onCloseClicked={editClose}
								/>
							) : (
								<ReservationDetail
									past={past}
									reservation={selectedReservation}
									onClose={() => selectReservation(null)}
									cancelReservation={cancel}
									editReservation={edit}
									editLoading={editLoading}
									myId={auth.userId}
								/>
							)
						) : (
							[
								<SeasonStats key="stats" />,
								//  <FavoriteClubs key="favorites" />,
								<AdWidget key="ads" location={AdLocation.ReservationSidebar} className="aBlock" />,
							]
						)}
					</div>
				</div>
			</div>
		</div>
	);
};

function mapStateToProps({ auth, reservation }: { auth: AuthState; reservation: ReservationState }) {
	return { auth, reservation };
}
export default withRedux(
	{ editReservation, cancelEditReservation, getReservationContext },
	mapStateToProps
)(Reservations);
